import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { z } from 'zod';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import { navContextSearchSchema } from '~/features/catalog-enrichment/utils/nav-context';

const RunProductJourneyPage = lazy(
  () => import('~/features/catalog-enrichment/pages/run-product-journey-page'),
);

/** S8 — `from`/`back`: this page is also reached from a product's own history (not just the
 *  batch Products tab it's nested under), so it needs the same origin trail as every other
 *  destination route. */
const searchSchema = z.object({
  ...navContextSearchSchema,
  /** The page's tab (absent = Attributes). */
  tab: z.enum(['scores', 'quality', 'journey']).optional().catch(undefined),
  /** Attributes tab's field search. */
  fq: z.string().optional().catch(undefined),
});

/**
 * One product in this batch: a header (image, title, ids, score, where it is, Edit / Re-score) and
 * four tabs in the URL — Attributes (every field Before | After), Scores, Quality checks, Journey.
 * Nested under the run shell, so it keeps the batch header/tabs.
 */
export const Route = createFileRoute(
  '/_authenticated/_tenant/catalog-enrichment/runs/$runId/products/$productId',
)({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const { runId, productId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  return (
    <Suspense fallback={<PageSkeleton />}>
      <RunProductJourneyPage
        runId={runId}
        productId={productId}
        tab={search.tab ?? 'attributes'}
        fieldQuery={search.fq ?? ''}
        onSearchChange={(patch, opts) => {
          void navigate({
            search: (prev) => ({ ...prev, ...patch }),
            replace: opts?.replace ?? false,
            resetScroll: false,
          });
        }}
      />
    </Suspense>
  );
}
